/**
 * TasksPage — Module Tâches
 * Vue Liste + Kanban, bouton Google Calendar, multi-structure.
 */

import React, { useState, useEffect, useCallback } from 'react';
import {
  CheckSquare, Plus, X, Trash2, Edit, Calendar, User,
  AlertTriangle, CheckCircle, Clock, Circle, Ban,
  Loader, Search, Building, Lock, List, LayoutGrid,
  CalendarPlus, ArrowUpDown, ChevronDown, ChevronRight,
} from 'lucide-react';
import { getTasksMultiStructures, createTask, updateTask, deleteTask, getPeople } from '../api';
import { useStructure } from '../contexts/StructureContext';
import { usePersonContext } from '../contexts/PersonContext';
import LoadingSpinner from '../components/LoadingSpinner';
import { useToast } from '../contexts/NotificationContext';

// ─── Config ───────────────────────────────────────────────────────────────────

const PRIORITY_CONFIG = {
  faible:  { label: 'Faible',  color: 'bg-gray-100 text-gray-500',     rank: 0 },
  normale: { label: 'Normale', color: 'bg-blue-100 text-blue-700',     rank: 1 },
  haute:   { label: 'Haute',   color: 'bg-orange-100 text-orange-700', rank: 2 },
  urgente: { label: 'Urgente', color: 'bg-red-100 text-red-700',       rank: 3 },
};

const STATUS_CONFIG = {
  'planifié': { label: 'Planifié', color: 'bg-violet-100 text-violet-700', border: 'border-violet-200', icon: Calendar },
  'à_faire':  { label: 'À faire',  color: 'bg-gray-100 text-gray-600',   border: 'border-gray-200',  icon: Circle },
  'en_cours': { label: 'En cours', color: 'bg-blue-100 text-blue-700',   border: 'border-blue-200',  icon: Clock },
  'terminé':  { label: 'Terminé',  color: 'bg-green-100 text-green-700', border: 'border-green-200', icon: CheckCircle },
  'annulé':   { label: 'Annulé',   color: 'bg-red-50 text-red-500',      border: 'border-red-100',   icon: Ban },
};

const COLUMNS = ['planifié', 'à_faire', 'en_cours', 'terminé', 'annulé'];

const EMPTY_FORM = {
  title: '', description: '', priority: 'normale', status: 'à_faire',
  dueDate: '', dueTime: '', showInCalendar: false, createdBy: '', assignedTo: '', structureIds: [], isPrivate: false, notes: '',
};

const fmtDate = (d) => d ? new Date(d).toLocaleDateString('fr-FR') : null;
const isOverdue = (t) =>
  t.dueDate && t.status !== 'terminé' && t.status !== 'annulé' &&
  new Date(t.dueDate) < new Date();

const getStructureLabel = (task) =>
  [...(task.structureNames || [])].sort((a, b) => a.localeCompare(b)).join(' / ') || '—';

// ─── Google Calendar ──────────────────────────────────────────────────────────

const buildGoogleCalendarUrl = (task) => {
  if (!task.dueDate) return null;
  const date = new Date(task.dueDate);
  // Evenement toute la journee : format YYYYMMDD/YYYYMMDD+1
  const pad = (n) => String(n).padStart(2, '0');
  const start = `${date.getFullYear()}${pad(date.getMonth() + 1)}${pad(date.getDate())}`;
  const next = new Date(date);
  next.setDate(next.getDate() + 1);
  const end = `${next.getFullYear()}${pad(next.getMonth() + 1)}${pad(next.getDate())}`;

  const params = new URLSearchParams({
    action: 'TEMPLATE',
    text: task.title,
    dates: `${start}/${end}`,
    details: [
      task.description,
      task.assignedTo ? `Assignee a : ${task.assignedTo}` : '',
      task.notes ? `Notes : ${task.notes}` : '',
    ].filter(Boolean).join('\n'),
  });
  return `https://calendar.google.com/calendar/render?${params.toString()}`;
};

// ─── Composant ────────────────────────────────────────────────────────────────

const TasksPage = ({ userRole, navPayload, onNavPayloadConsumed }) => {
  const { currentStructure, structures } = useStructure();
  const { currentPerson } = usePersonContext();
  const toast = useToast();

  const [tasks, setTasks] = useState([]);
  const [people, setPeople] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchText, setSearchText] = useState('');
  const [filterPriority, setFilterPriority] = useState('');
  const [filterAssignedTo, setFilterAssignedTo] = useState('');
  const [filterStructure, setFilterStructure] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [selectedTask, setSelectedTask] = useState(null);

  // Vue : 'list' ou 'kanban'
  const [viewMode, setViewMode] = useState(() => localStorage.getItem('tasks-view') || 'list');
  // Tri vue liste
  const [sortField, setSortField] = useState('dueDate');
  const [sortDir, setSortDir] = useState('asc');
  // Sections repliees en vue liste (par statut)
  const [collapsedSections, setCollapsedSections] = useState({ terminé: true, annulé: true });

  const multiStructure = structures.length > 1;

  useEffect(() => { localStorage.setItem('tasks-view', viewMode); }, [viewMode]);

  const fetchTasks = useCallback(async () => {
    if (!structures.length) return;
    try {
      setLoading(true);
      const ids = structures.map(s => s.id);
      const res = await getTasksMultiStructures(ids);
      setTasks(res.data);
    } catch (error) {
      console.error('[Tasks] fetch error:', error);
      toast.error('Erreur lors du chargement des taches');
    } finally {
      setLoading(false);
    }
  }, [structures]);

  useEffect(() => { fetchTasks(); }, [fetchTasks]);

  useEffect(() => {
    if (currentStructure) {
      getPeople(currentStructure.id).then(r => setPeople(r.data)).catch(() => setPeople([]));
    }
  }, [currentStructure]);

  // Auto-ouverture détail tâche depuis recherche globale (navPayload.taskId)
  useEffect(() => {
    if (!navPayload?.taskId || tasks.length === 0) return;
    const t = tasks.find(x => x.id === navPayload.taskId);
    if (t) {
      setSelectedTask(t);
      onNavPayloadConsumed?.();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [navPayload, tasks]);

  useEffect(() => {
    if (currentPerson) {
      setFilterAssignedTo(`${currentPerson.firstName} ${currentPerson.lastName}`.trim());
    }
  }, [currentPerson]);

  const getPersonName = () =>
    currentPerson ? `${currentPerson.firstName} ${currentPerson.lastName}`.trim() : '';

  const resetForm = () => { setForm(EMPTY_FORM); setEditingId(null); setShowForm(false); };

  const openNewForm = (defaultStatus = 'à_faire') => {
    setForm({
      ...EMPTY_FORM,
      status: defaultStatus,
      createdBy: getPersonName(),
      structureIds: filterStructure
        ? [filterStructure]
        : currentStructure ? [currentStructure.id] : [],
    });
    setEditingId(null);
    setShowForm(true);
    setSelectedTask(null);
  };

  const handleEdit = (task) => {
    setForm({
      title: task.title,
      description: task.description || '',
      priority: task.priority,
      status: task.status,
      dueDate: task.dueDate ? task.dueDate.split('T')[0] : '',
      dueTime: task.dueTime || '',
      showInCalendar: task.showInCalendar || false,
      recurrence: task.recurrence || '',
      createdBy: task.createdBy || '',
      assignedTo: task.assignedTo || '',
      structureIds: task.structureIds || [],
      isPrivate: task.isPrivate || false,
      notes: task.notes || '',
    });
    setEditingId(task.id);
    setShowForm(true);
    setSelectedTask(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.title.trim()) return;
    setSaving(true);
    try {
      const payload = { ...form, dueDate: form.dueDate || null };
      if (editingId) {
        const res = await updateTask(editingId, payload);
        setTasks(prev => prev.map(t => t.id === editingId ? res.data : t));
        if (selectedTask?.id === editingId) setSelectedTask(res.data);
        toast.success('Tache modifiee');
      } else {
        const res = await createTask(payload);
        setTasks(prev => [...prev, res.data]);
        toast.success('Tache creee');
      }
      resetForm();
    } catch (error) {
      console.error('[Tasks] save error:', error);
      toast.error('Erreur lors de la sauvegarde de la tache');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Supprimer cette tâche ?')) return;
    try {
      await deleteTask(id);
      setTasks(prev => prev.filter(t => t.id !== id));
      if (selectedTask?.id === id) setSelectedTask(null);
      toast.success('Tache supprimee');
    } catch (error) {
      console.error('[Tasks] delete error:', error);
      toast.error('Erreur lors de la suppression de la tache');
    }
  };

  const handleStatusChange = async (task, newStatus) => {
    try {
      const res = await updateTask(task.id, { status: newStatus });
      setTasks(prev => prev.map(t => t.id === task.id ? res.data : t));
      if (selectedTask?.id === task.id) setSelectedTask(res.data);
      toast.success('Statut mis a jour');
    } catch (error) {
      console.error('[Tasks] status change error:', error);
      toast.error('Erreur lors du changement de statut');
    }
  };

  const toggleStructureInForm = (sId) => {
    setForm(f => ({
      ...f,
      structureIds: f.structureIds.includes(sId)
        ? f.structureIds.filter(id => id !== sId)
        : [...f.structureIds, sId],
    }));
  };

  const toggleSection = (status) => {
    setCollapsedSections(prev => ({ ...prev, [status]: !prev[status] }));
  };

  // Filtrage
  const filtered = tasks.filter(t => {
    if (searchText &&
      !t.title.toLowerCase().includes(searchText.toLowerCase()) &&
      !(t.assignedTo || '').toLowerCase().includes(searchText.toLowerCase())) return false;
    if (filterPriority && t.priority !== filterPriority) return false;
    if (filterAssignedTo && (t.assignedTo || '').toLowerCase() !== filterAssignedTo.toLowerCase()) return false;
    if (filterStructure && !(t.structureIds || []).includes(filterStructure)) return false;
    return true;
  });

  const allAssignees = [...new Set(tasks.map(t => t.assignedTo).filter(Boolean))].sort();

  // Tri pour vue liste
  const sortTasks = (list) => {
    return [...list].sort((a, b) => {
      let cmp = 0;
      switch (sortField) {
        case 'priority':
          cmp = (PRIORITY_CONFIG[b.priority]?.rank || 0) - (PRIORITY_CONFIG[a.priority]?.rank || 0);
          break;
        case 'dueDate':
          if (!a.dueDate && !b.dueDate) cmp = 0;
          else if (!a.dueDate) cmp = 1;
          else if (!b.dueDate) cmp = -1;
          else cmp = new Date(a.dueDate) - new Date(b.dueDate);
          break;
        case 'title':
          cmp = a.title.localeCompare(b.title);
          break;
        case 'assignedTo':
          cmp = (a.assignedTo || '').localeCompare(b.assignedTo || '');
          break;
        default:
          break;
      }
      return sortDir === 'desc' ? -cmp : cmp;
    });
  };

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDir('asc');
    }
  };

  const inputCls = 'w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary';

  // ─── Bouton Google Calendar ─────────────────────────────────────────────────

  const GoogleCalBtn = ({ task, size = 13, className = '' }) => {
    const url = buildGoogleCalendarUrl(task);
    if (!url) return null;
    return (
      <a href={url} target="_blank" rel="noopener noreferrer"
        onClick={(e) => e.stopPropagation()}
        title="Ajouter a Google Calendar"
        className={`text-primary/50 hover:text-primary transition ${className}`}>
        <CalendarPlus size={size} />
      </a>
    );
  };

  // ─── Vue Liste ──────────────────────────────────────────────────────────────

  const SortHeader = ({ field, children, className = '' }) => (
    <th
      onClick={() => handleSort(field)}
      className={`px-3 py-2 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider cursor-pointer hover:text-gray-700 select-none ${className}`}>
      <span className="flex items-center gap-1">
        {children}
        {sortField === field && (
          <ArrowUpDown size={11} className="text-primary" />
        )}
      </span>
    </th>
  );

  const renderListView = () => {
    const statusOrder = ['en_cours', 'à_faire', 'planifié', 'terminé', 'annulé'];

    return (
      <div className="flex-1 overflow-y-auto">
        {statusOrder.map(status => {
          const cfg = STATUS_CONFIG[status];
          const StatusIcon = cfg.icon;
          const statusTasks = sortTasks(filtered.filter(t => t.status === status));
          const isCollapsed = collapsedSections[status];

          if (statusTasks.length === 0) return null;

          return (
            <div key={status} className="mb-1">
              {/* Section header */}
              <button
                onClick={() => toggleSection(status)}
                className={`w-full flex items-center gap-2 px-4 py-2 ${cfg.color} border-b ${cfg.border} text-sm font-semibold sticky top-0 z-10`}>
                {isCollapsed ? <ChevronRight size={14} /> : <ChevronDown size={14} />}
                <StatusIcon size={14} />
                {cfg.label}
                <span className="text-xs font-normal opacity-70">({statusTasks.length})</span>
              </button>

              {!isCollapsed && (
                <table className="w-full">
                  <thead>
                    <tr className="bg-gray-50/80">
                      <SortHeader field="priority" className="w-20">Priorité</SortHeader>
                      <SortHeader field="title">Titre</SortHeader>
                      <SortHeader field="dueDate" className="w-28 hidden sm:table-cell">Echéance</SortHeader>
                      <SortHeader field="assignedTo" className="w-32 hidden md:table-cell">Assigné</SortHeader>
                      {multiStructure && <th className="px-3 py-2 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider w-28 hidden lg:table-cell">Structure</th>}
                      <th className="px-3 py-2 w-20 text-center text-xs font-semibold text-gray-500 uppercase tracking-wider">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {statusTasks.map(task => {
                      const overdue = isOverdue(task);
                      const pCfg = PRIORITY_CONFIG[task.priority] || PRIORITY_CONFIG.normale;
                      const isSelected = selectedTask?.id === task.id;

                      return (
                        <tr key={task.id}
                          onClick={() => setSelectedTask(isSelected ? null : task)}
                          className={`cursor-pointer hover:bg-gray-50 transition text-sm ${
                            overdue ? 'bg-red-50/50' : isSelected ? 'bg-primary/5' : ''
                          }`}>
                          <td className="px-3 py-2.5">
                            <span className={`text-xs px-1.5 py-0.5 rounded-full font-medium ${pCfg.color}`}>
                              {pCfg.label}
                            </span>
                          </td>
                          <td className="px-3 py-2.5">
                            <div className="flex items-center gap-1.5">
                              <span className="font-medium text-gray-900">{task.title}</span>
                              {task.isPrivate && <Lock size={11} className="text-indigo-400 shrink-0" />}
                              {overdue && <AlertTriangle size={12} className="text-red-500 shrink-0" />}
                            </div>
                          </td>
                          <td className="px-3 py-2.5 hidden sm:table-cell">
                            {task.dueDate && (
                              <span className={`text-xs ${overdue ? 'text-red-500 font-medium' : 'text-gray-500'}`}>
                                {fmtDate(task.dueDate)}
                              </span>
                            )}
                          </td>
                          <td className="px-3 py-2.5 hidden md:table-cell">
                            <span className="text-xs text-gray-500">{task.assignedTo || '—'}</span>
                          </td>
                          {multiStructure && (
                            <td className="px-3 py-2.5 hidden lg:table-cell">
                              <span className="text-xs text-primary/70">{getStructureLabel(task)}</span>
                            </td>
                          )}
                          <td className="px-3 py-2.5 text-center">
                            <div className="flex items-center justify-center gap-1" onClick={e => e.stopPropagation()}>
                              <GoogleCalBtn task={task} size={14} />
                              {status !== 'terminé' && (
                                <button
                                  onClick={() => handleStatusChange(task, 'terminé')}
                                  title="Marquer comme termine"
                                  className="text-gray-300 hover:text-green-500 transition">
                                  <CheckCircle size={16} />
                                </button>
                              )}
                              <button
                                onClick={() => handleEdit(task)}
                                title="Modifier"
                                className="text-gray-300 hover:text-gray-600 transition">
                                <Edit size={14} />
                              </button>
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              )}
            </div>
          );
        })}
        {filtered.length === 0 && (
          <p className="text-center text-gray-400 py-12 text-sm">Aucune tache trouvee</p>
        )}
      </div>
    );
  };

  // ─── Carte tâche (kanban) ───────────────────────────────────────────────────

  const renderCard = (task) => {
    const overdue = isOverdue(task);
    const pCfg = PRIORITY_CONFIG[task.priority] || PRIORITY_CONFIG.normale;
    const isSelected = selectedTask?.id === task.id;
    const structureLabel = getStructureLabel(task);

    return (
      <div
        key={task.id}
        onClick={() => setSelectedTask(isSelected ? null : task)}
        className={`bg-white rounded-xl border p-3 cursor-pointer transition-all hover:shadow-md text-sm ${
          overdue ? 'border-red-200' : isSelected ? 'border-primary shadow-md' : 'border-gray-200'
        }`}
      >
        <div className="flex items-start justify-between gap-1 mb-2">
          <p className="font-medium text-gray-900 leading-tight flex-1">{task.title}</p>
          <div className="flex items-center gap-1 shrink-0">
            <GoogleCalBtn task={task} size={12} />
            {task.isPrivate && <Lock size={12} className="text-indigo-400" title="Visible uniquement par vous" />}
          </div>
        </div>
        <div className="flex flex-wrap gap-1.5 items-center">
          <span className={`text-xs px-1.5 py-0.5 rounded-full font-medium ${pCfg.color}`}>{pCfg.label}</span>
          {overdue && <span className="flex items-center gap-0.5 text-xs text-red-500 font-medium"><AlertTriangle size={10} />Retard</span>}
          {task.dueDate && !overdue && <span className="flex items-center gap-0.5 text-xs text-gray-400"><Calendar size={10} />{fmtDate(task.dueDate)}</span>}
          {task.assignedTo && <span className="flex items-center gap-0.5 text-xs text-gray-500 ml-auto"><User size={10} />{task.assignedTo}</span>}
        </div>
        {structureLabel !== '—' && (
          <div className="mt-1.5 flex items-center gap-1 text-xs text-primary/70">
            <Building size={10} />
            <span>{structureLabel}</span>
          </div>
        )}
      </div>
    );
  };

  // ─── Kanban colonne ─────────────────────────────────────────────────────────

  const renderColumn = (colStatus) => {
    const cfg = STATUS_CONFIG[colStatus];
    const StatusIcon = cfg.icon;
    const colTasks = filtered.filter(t => t.status === colStatus);
    const doGroup = multiStructure && !filterStructure;

    let content;
    if (doGroup && colTasks.length > 0) {
      const groups = {};
      for (const task of colTasks) {
        const label = getStructureLabel(task);
        if (!groups[label]) groups[label] = [];
        groups[label].push(task);
      }
      const sortedGroupKeys = Object.keys(groups).sort((a, b) => a.localeCompare(b));

      content = (
        <div className="flex-1 p-2 space-y-3">
          {sortedGroupKeys.map(label => (
            <div key={label}>
              <div className="flex items-center gap-1.5 px-1 mb-1.5">
                <Building size={11} className="text-primary/60" />
                <span className="text-xs font-semibold text-primary/70 uppercase tracking-wide">{label}</span>
                <span className="text-xs text-gray-300">({groups[label].length})</span>
              </div>
              <div className="space-y-2">
                {groups[label].map(t => renderCard(t))}
              </div>
            </div>
          ))}
        </div>
      );
    } else {
      content = (
        <div className="flex-1 p-2 space-y-2">
          {colTasks.map(t => renderCard(t))}
          {colTasks.length === 0 && (
            <p className="text-xs text-center text-gray-300 py-4">Vide</p>
          )}
        </div>
      );
    }

    return (
      <div key={colStatus} className={`rounded-xl border ${cfg.border} bg-gray-50/60 flex flex-col min-h-48`}>
        <div className={`flex items-center justify-between px-3 py-2.5 rounded-t-xl ${cfg.color} border-b ${cfg.border}`}>
          <div className="flex items-center gap-1.5 font-semibold text-sm">
            <StatusIcon size={14} />
            {cfg.label}
            <span className="text-xs font-normal opacity-70">({colTasks.length})</span>
          </div>
          <button onClick={() => openNewForm(colStatus)}
            className="opacity-60 hover:opacity-100 transition p-0.5 rounded"
            title={`Ajouter dans "${cfg.label}"`}>
            <Plus size={14} />
          </button>
        </div>
        {content}
      </div>
    );
  };

  // ─── Render ─────────────────────────────────────────────────────────────────

  if (loading) {
    return <div className="flex justify-center items-center h-64"><LoadingSpinner size="large" text="Chargement..." /></div>;
  }

  return (
    <div className="flex h-full gap-0">
      {/* Zone principale */}
      <div className={`flex flex-col flex-1 min-w-0 ${selectedTask ? 'hidden lg:flex' : ''}`}>

        {/* Header */}
        <div className="p-4 border-b border-gray-200 bg-white">
          <div className="flex items-center justify-between mb-3">
            <h1 className="text-lg font-bold text-gray-900 flex items-center gap-2">
              <CheckSquare size={20} className="text-primary" />
              Tâches
              <span className="text-sm font-normal text-gray-400">({filtered.length})</span>
            </h1>
            <div className="flex items-center gap-2">
              {/* Toggle vue */}
              <div className="flex border border-gray-200 rounded-lg overflow-hidden">
                <button
                  onClick={() => setViewMode('list')}
                  className={`p-1.5 transition ${viewMode === 'list' ? 'bg-primary text-white' : 'text-gray-400 hover:text-gray-600'}`}
                  title="Vue liste">
                  <List size={16} />
                </button>
                <button
                  onClick={() => setViewMode('kanban')}
                  className={`p-1.5 transition ${viewMode === 'kanban' ? 'bg-primary text-white' : 'text-gray-400 hover:text-gray-600'}`}
                  title="Vue kanban">
                  <LayoutGrid size={16} />
                </button>
              </div>
              <button onClick={() => openNewForm()}
                className="flex items-center gap-1.5 bg-primary text-white px-3 py-1.5 rounded-lg text-sm font-medium hover:bg-primary-dark transition">
                <Plus size={15} />Nouvelle
              </button>
            </div>
          </div>

          {/* Filtres */}
          <div className="flex flex-wrap gap-2">
            <div className="relative flex-1 min-w-36">
              <Search size={13} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400" />
              <input className="w-full border border-gray-200 rounded-lg pl-7 pr-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-primary/30"
                placeholder="Rechercher..." value={searchText} onChange={e => setSearchText(e.target.value)} />
            </div>
            <select className="border border-gray-200 rounded-lg px-2 py-1.5 text-sm focus:outline-none"
              value={filterPriority} onChange={e => setFilterPriority(e.target.value)}>
              <option value="">Toutes priorités</option>
              {Object.entries(PRIORITY_CONFIG).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
            </select>
            <select className="border border-gray-200 rounded-lg px-2 py-1.5 text-sm focus:outline-none min-w-28"
              value={filterAssignedTo} onChange={e => setFilterAssignedTo(e.target.value)}>
              <option value="">Tous assignés</option>
              {allAssignees.map(name => <option key={name} value={name}>{name}</option>)}
            </select>
            {multiStructure && (
              <select className="border border-gray-200 rounded-lg px-2 py-1.5 text-sm focus:outline-none min-w-28"
                value={filterStructure} onChange={e => setFilterStructure(e.target.value)}>
                <option value="">Toutes structures</option>
                {structures.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
              </select>
            )}
            {(filterAssignedTo || filterStructure) && (
              <button onClick={() => { setFilterAssignedTo(''); setFilterStructure(''); }}
                className="flex items-center gap-1 text-xs text-gray-500 hover:text-gray-700 px-2 border border-gray-200 rounded-lg">
                <X size={11} />Effacer filtres
              </button>
            )}
          </div>
        </div>

        {/* Contenu : Liste ou Kanban */}
        {viewMode === 'list' ? renderListView() : (
          <div className="flex-1 overflow-y-auto p-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4 items-start">
              {COLUMNS.map(colStatus => renderColumn(colStatus))}
            </div>
          </div>
        )}
      </div>

      {/* Panneau détail */}
      {selectedTask && (
        <div className="w-full lg:w-80 flex-shrink-0 border-l border-gray-200 bg-white flex flex-col overflow-hidden">
          <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200">
            <div className="flex items-center gap-2 min-w-0">
              <h3 className="font-semibold text-gray-900 text-sm truncate">{selectedTask.title}</h3>
              {selectedTask.isPrivate && <Lock size={13} className="text-indigo-400 shrink-0" title="Visible uniquement par vous" />}
            </div>
            <button onClick={() => setSelectedTask(null)} className="text-gray-400 hover:text-gray-600 shrink-0 ml-2">
              <X size={18} />
            </button>
          </div>

          <div className="flex-1 overflow-y-auto p-4 space-y-5 text-sm">
            {/* Changement statut */}
            <div>
              <p className="text-xs text-gray-400 mb-2">Statut</p>
              <div className="flex flex-wrap gap-1.5">
                {COLUMNS.map(s => {
                  const cfg = STATUS_CONFIG[s];
                  const Icon = cfg.icon;
                  return (
                    <button key={s}
                      onClick={() => handleStatusChange(selectedTask, s)}
                      className={`flex items-center gap-1 text-xs px-2.5 py-1 rounded-full font-medium border transition cursor-pointer ${
                        selectedTask.status === s
                          ? `${cfg.color} border-transparent`
                          : 'border-gray-200 text-gray-500 hover:border-gray-300'
                      }`}>
                      <Icon size={10} />{cfg.label}
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Infos */}
            <div className="space-y-2 text-xs text-gray-600">
              {[
                ['Priorité', PRIORITY_CONFIG[selectedTask.priority]?.label],
                ['Créée par', selectedTask.createdBy],
                ['Assignée à', selectedTask.assignedTo],
                ['Échéance', fmtDate(selectedTask.dueDate)],
                ['Terminée le', selectedTask.completedAt ? fmtDate(selectedTask.completedAt) : null],
                ['Structures', getStructureLabel(selectedTask) !== '—' ? getStructureLabel(selectedTask) : null],
                ...(selectedTask.isPrivate ? [['Visibilité', 'Visible uniquement par vous']] : []),
              ].filter(([, v]) => v).map(([l, v]) => (
                <div key={l} className="flex gap-2">
                  <span className="text-gray-400 w-24 shrink-0">{l}</span>
                  <span className={`font-medium ${l === 'Visibilité' ? 'text-amber-600' : 'text-gray-800'}`}>{v}</span>
                </div>
              ))}
            </div>

            {/* Bouton Google Calendar */}
            {selectedTask.dueDate && (
              <a href={buildGoogleCalendarUrl(selectedTask)} target="_blank" rel="noopener noreferrer"
                className="flex items-center gap-2 w-full px-3 py-2 border border-primary/20 rounded-lg text-sm text-primary hover:bg-primary/5 transition">
                <CalendarPlus size={15} />
                Ajouter a Google Calendar
              </a>
            )}

            {selectedTask.description && (
              <div>
                <p className="text-xs text-gray-400 mb-1">Description</p>
                <p className="text-gray-700 whitespace-pre-wrap bg-gray-50 rounded-lg p-3 text-xs">{selectedTask.description}</p>
              </div>
            )}
            {selectedTask.notes && (
              <div>
                <p className="text-xs text-gray-400 mb-1">Notes</p>
                <p className="text-gray-700 whitespace-pre-wrap bg-gray-50 rounded-lg p-3 text-xs">{selectedTask.notes}</p>
              </div>
            )}
          </div>

          <div className="border-t border-gray-200 p-3 flex gap-2">
            <button onClick={() => handleEdit(selectedTask)}
              className="flex-1 flex items-center justify-center gap-1.5 py-2 border border-gray-200 rounded-lg text-sm text-gray-600 hover:bg-gray-50 transition">
              <Edit size={14} />Modifier
            </button>
            <button onClick={() => handleDelete(selectedTask.id)}
              className="flex items-center justify-center gap-1.5 px-3 py-2 border border-red-200 rounded-lg text-sm text-red-500 hover:bg-red-50 transition">
              <Trash2 size={14} />
            </button>
          </div>
        </div>
      )}

      {/* Modal création/édition */}
      {showForm && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-md max-h-[90vh] flex flex-col">
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
              <h2 className="text-lg font-semibold text-gray-800">
                {editingId ? 'Modifier la tâche' : 'Nouvelle tâche'}
              </h2>
              <button onClick={resetForm} className="text-gray-400 hover:text-gray-600"><X size={20} /></button>
            </div>
            <form onSubmit={handleSubmit} className="overflow-y-auto p-6 space-y-4">

              {structures.length > 0 && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Structures</label>
                  {structures.length === 1 ? (
                    <p className="text-sm text-gray-600 bg-gray-50 rounded-lg px-3 py-2">
                      <Building size={13} className="inline mr-1.5 text-primary" />{structures[0].name}
                    </p>
                  ) : (
                    <div className="space-y-1.5">
                      {structures.map(s => (
                        <label key={s.id}
                          className={`flex items-center gap-2.5 px-3 py-2 rounded-lg border cursor-pointer transition ${
                            form.structureIds.includes(s.id)
                              ? 'border-primary bg-primary/5'
                              : 'border-gray-200 hover:border-gray-300'
                          }`}>
                          <input type="checkbox"
                            checked={form.structureIds.includes(s.id)}
                            onChange={() => toggleStructureInForm(s.id)}
                            className="w-4 h-4 accent-primary" />
                          <Building size={13} className="text-primary/60" />
                          <span className="text-sm text-gray-700">{s.name}</span>
                        </label>
                      ))}
                    </div>
                  )}
                </div>
              )}

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Titre <span className="text-red-500">*</span></label>
                <input className={inputCls} value={form.title}
                  onChange={e => setForm(f => ({ ...f, title: e.target.value }))} required autoFocus />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Priorité</label>
                  <select className={inputCls} value={form.priority}
                    onChange={e => setForm(f => ({ ...f, priority: e.target.value }))}>
                    {Object.entries(PRIORITY_CONFIG).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Statut</label>
                  <select className={inputCls} value={form.status}
                    onChange={e => setForm(f => ({ ...f, status: e.target.value }))}>
                    {COLUMNS.map(s => <option key={s} value={s}>{STATUS_CONFIG[s].label}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Échéance</label>
                  <input type="date" className={inputCls} value={form.dueDate}
                    onChange={e => setForm(f => ({ ...f, dueDate: e.target.value }))} />
                  {form.dueDate && (
                    <input type="time" className={`${inputCls} mt-1.5`} value={form.dueTime}
                      onChange={e => setForm(f => ({ ...f, dueTime: e.target.value }))}
                      placeholder="Heure (optionnel)" />
                  )}
                  {form.dueDate && (
                    <>
                      <label className="flex items-center gap-2 text-xs text-gray-500 mt-1.5">
                        <input type="checkbox" checked={form.showInCalendar}
                          onChange={e => setForm(f => ({ ...f, showInCalendar: e.target.checked }))} />
                        Afficher sur le calendrier
                      </label>
                      {form.showInCalendar && (
                        <select className="mt-1.5 w-full border border-gray-200 rounded-lg px-3 py-1.5 text-sm"
                          value={form.recurrence || ''} onChange={e => setForm(f => ({ ...f, recurrence: e.target.value || null }))}>
                          <option value="">Une seule fois</option>
                          <option value="daily">Tous les jours</option>
                          <option value="weekly">Toutes les semaines</option>
                          <option value="biweekly">Toutes les 2 semaines</option>
                          <option value="monthly">Tous les mois (même date)</option>
                          {form.dueDate && (() => {
                            const d = new Date(form.dueDate);
                            const days = ['dimanche','lundi','mardi','mercredi','jeudi','vendredi','samedi'];
                            const nth = Math.ceil(d.getDate() / 7);
                            const ordinals = ['','1er','2ème','3ème','4ème','5ème'];
                            return <option value="monthly_weekday">{`Tous les mois (${ordinals[nth]} ${days[d.getDay()]})`}</option>;
                          })()}
                        </select>
                      )}
                    </>
                  )}
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Assignée à</label>
                  {people.length > 0 ? (
                    <select className={inputCls} value={form.assignedTo}
                      onChange={e => setForm(f => ({ ...f, assignedTo: e.target.value }))}>
                      <option value="">—</option>
                      {people.map(p => (
                        <option key={p.id} value={`${p.firstName} ${p.lastName}`.trim()}>
                          {p.firstName} {p.lastName}
                        </option>
                      ))}
                    </select>
                  ) : (
                    <input className={inputCls} value={form.assignedTo}
                      onChange={e => setForm(f => ({ ...f, assignedTo: e.target.value }))} placeholder="Prénom Nom" />
                  )}
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Créée par</label>
                <input className={inputCls} value={form.createdBy}
                  onChange={e => setForm(f => ({ ...f, createdBy: e.target.value }))} />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                <textarea className={inputCls} rows={3} value={form.description}
                  onChange={e => setForm(f => ({ ...f, description: e.target.value }))} />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Notes</label>
                <textarea className={inputCls} rows={2} value={form.notes}
                  onChange={e => setForm(f => ({ ...f, notes: e.target.value }))} />
              </div>

              <label className="flex items-center gap-3 p-3 bg-indigo-50 border border-indigo-200 rounded-lg cursor-pointer">
                <input type="checkbox" checked={form.isPrivate}
                  onChange={e => setForm(f => ({ ...f, isPrivate: e.target.checked }))}
                  className="w-4 h-4 accent-indigo-500" />
                <div>
                  <p className="text-sm font-medium text-indigo-800 flex items-center gap-1.5">
                    <Lock size={13} />Visible uniquement par moi
                  </p>
                  <p className="text-xs text-indigo-600">Seul votre compte pourra voir cette tâche.</p>
                </div>
              </label>

              <div className="flex gap-2 justify-end pt-2">
                <button type="button" onClick={resetForm}
                  className="px-4 py-2 border border-gray-200 rounded-lg text-sm text-gray-600 hover:bg-gray-50">
                  Annuler
                </button>
                <button type="submit" disabled={saving}
                  className="flex items-center gap-2 px-4 py-2 bg-primary text-white rounded-lg text-sm font-medium hover:bg-primary-dark disabled:opacity-50">
                  {saving && <Loader size={14} className="animate-spin" />}
                  {editingId ? 'Enregistrer' : 'Créer'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* FAB mobile */}
      {!showForm && (
        <button onClick={() => openNewForm()}
          className="lg:hidden fixed bottom-6 right-6 z-40 bg-primary text-white rounded-full p-4 shadow-lg hover:bg-primary-dark transition">
          <Plus size={22} />
        </button>
      )}
    </div>
  );
};

export default TasksPage;
